import { useContext } from 'react'

import { launchModal } from '@domain-group/javascript-utils'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as brandProviderActions from '@redux/modules/brandProvider'

import PageContext from '@contexts/pageContext'

import defaultFormState, { requiredFields } from '@models/brandProvider'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'
import type { BrandProviderModel, BrandProviderRequestOptions } from '@models/types'

export const generateUrls = (brandProvider: Partial<BrandProviderModel> = {}) => {
  const { brand_id } = brandProvider

  return {
    editBrandProviderUrl: `#/systemManager/brands/${brand_id}/Edit`,
  }
}

type CreateBrandProviderParams = {
  brandProviderParams: Partial<BrandProviderModel>,
  dispatch: AppDispatch,
  requestOptions?: BrandProviderRequestOptions,
}

const createBrandProvider = (params: CreateBrandProviderParams) => {
  const { dispatch, brandProviderParams, requestOptions } = params
  const { createBrandProvider: createFn } = brandProviderActions

  return dispatch(createFn(brandProviderParams, requestOptions))
}

export type BulkCreateBrandProvidersParams = {
  bulkCreateParams: { brand_id?: number, brand_ids?: number[], provider_id?: number, provider_ids?:number[] },
  dispatch: AppDispatch,
  requestOptions?: BrandProviderRequestOptions,
}

const bulkCreateBrandProviders = (params: BulkCreateBrandProvidersParams) => {
  const { bulkCreateParams, dispatch, requestOptions } = params

  const { bulkCreateBrandProviders: bulkCreateFn } = brandProviderActions

  return dispatch(bulkCreateFn(bulkCreateParams, requestOptions))
}

type DeleteBrandProviderParams = {
  brandProvider: DeleteParams<BrandProviderModel>,
  dispatch: AppDispatch,
}

const deleteBrandProvider = (params: DeleteBrandProviderParams) => {
  const { dispatch, brandProvider } = params
  const { deleteBrandProvider: deleteFn } = brandProviderActions

  return dispatch(deleteFn(brandProvider))
}

export type ShowCreateBrandProviderModal = {
  brandProvider: Partial<BrandProviderModel>,
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useBrandProviderForm(
  brandProvider: Partial<BrandProviderModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const brandProviderForm = useForm(
    defaultFormState,
    { entity: brandProvider, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [brandProvider.id, brandProvider.cache_key],
  )

  return {
    ...brandProviderForm,
  }
}

export const useRelations = (brandProvider: Partial<BrandProviderModel> = {}) => {
  const { brand_id, provider_id } = brandProvider

  const { brands, providers } = useSelector(reduxState => reduxState.entities)

  const brand = brands[brand_id!]
  const provider = providers[provider_id!]

  return {
    brand,
    provider,
  }
}

function useBrandProvider(initEntity: Partial<BrandProviderModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: brandProvider }: { entity: BrandProviderModel } = useLatestEntity(initEntity, 'brandProviders')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { brand, provider } = useRelations(brandProvider)

  const {
    creating, deleting, loading,
  } = useSelector(reduxState => reduxState.brandProviders)

  return {
    brand,
    brandProvider,
    callbacks: {
      addBrandProvider: () => launchModal({
        callbacks,
        modalKey: 'BulkCreateBrandProviderModal',
        payload: { brandProvider },
      }),
      bulkCreateBrandProviders: (
        bulkCreateParams: BulkCreateBrandProvidersParams['bulkCreateParams'],
        requestOptions?: BrandProviderRequestOptions,
      ) => (
        bulkCreateBrandProviders({ bulkCreateParams, dispatch, requestOptions })
      ),
      createBrandProvider: (
        brandProviderParams: Partial<BrandProviderModel>,
        entityOptions?: BrandProviderRequestOptions,
      ) => (
        createBrandProvider({ brandProviderParams, dispatch, requestOptions: entityOptions })
      ),
      deleteBrandProvider: () => deleteBrandProvider({ dispatch, brandProvider }),
    },
    creating,
    deleting,
    loading,
    provider,
  }
}

export default useBrandProvider
